import axiosInstance from '../axiosConfig';

/**
 * Generic CRUD factory — eliminates ~200 lines of identical boilerplate.
 * Each resource gets getAll, getOne, create, update, remove.
 */
const crud = (base) => ({
  getAll:  (params = {}) => axiosInstance.get(base, { params }).then(r => r.data),
  getOne:  (id)          => axiosInstance.get(`${base}/${id}`).then(r => r.data),
  create:  (data)        => axiosInstance.post(base, data).then(r => r.data),
  update:  (id, data)    => axiosInstance.put(`${base}/${id}`, data).then(r => r.data),
  remove:  (id)          => axiosInstance.delete(`${base}/${id}`).then(r => r.data),
});

const adminService = {
  // Dashboard
  getDashboardStats: () => axiosInstance.get('/admin/dashboard').then(r => r.data),

  // Core resources (admin-namespaced)
  users:      crud('/admin/users'),
  blogs:      crud('/admin/blogs'),
  books:      crud('/admin/books'),
  categories: crud('/admin/categories'),

  // Ecommerce resources (public + admin endpoints)
  orders: {
    ...crud('/orders'),
    updateStatus: (id, data) => axiosInstance.put(`/orders/${id}/status`, data).then(r => r.data),
    getStats:     (params)   => axiosInstance.get('/orders/stats', { params }).then(r => r.data),
  },
  bundles:         crud('/bundles'),
  storeCategories: crud('/store-categories'),
  quotes: {
    getAll:       (params = {}) => axiosInstance.get('/quotes', { params }).then(r => r.data),
    updateStatus: (id, data)    => axiosInstance.put(`/quotes/${id}/status`, data).then(r => r.data),
  },
  coupons: crud('/coupons'),

  // Book Resources — free teaching materials, keyed by phase+course.
  // See docs/plans/book-resources.md. create/replaceFile use FormData +
  // onUploadProgress (a 100MB file over a school connection takes minutes)
  // and timeout:0, since axiosInstance's shared 15s timeout would abort
  // a large upload mid-flight and leave a partial file on disk.
  bookResources: {
    listCourses: () => axiosInstance.get('/admin/book-resources/courses').then((r) => r.data),
    list: (phase, course) =>
      axiosInstance.get('/admin/book-resources', { params: { phase, course } }).then((r) => r.data),
    create: (formData, onUploadProgress) =>
      axiosInstance.postForm('/admin/book-resources', formData, { onUploadProgress, timeout: 0 }).then((r) => r.data),
    update: (id, data) => axiosInstance.put(`/admin/book-resources/${id}`, data).then((r) => r.data),
    remove: (id) => axiosInstance.delete(`/admin/book-resources/${id}`).then((r) => r.data),
    reorder: (payload) => axiosInstance.put('/admin/book-resources/reorder', payload).then((r) => r.data),
    replaceFile: (id, formData, onUploadProgress) =>
      axiosInstance.postForm(`/admin/book-resources/${id}/file`, formData, { onUploadProgress, timeout: 0 }).then((r) => r.data),
  },

  // ── Backward-compatible aliases ───────────────────────────────────────
  // These map old flat method names to the new nested structure so that
  // existing admin components don't break until they're migrated.
  getUsers:            (params) => adminService.users.getAll(params),
  getUser:             (id)     => adminService.users.getOne(id),
  createUser:          (d)      => adminService.users.create(d),
  updateUser:          (id, d)  => adminService.users.update(id, d),
  deleteUser:          (id)     => adminService.users.remove(id),

  getBlogs:            (params) => adminService.blogs.getAll(params),
  getBlog:             (id)     => adminService.blogs.getOne(id),
  createBlog:          (d)      => adminService.blogs.create(d),
  updateBlog:          (id, d)  => adminService.blogs.update(id, d),
  deleteBlog:          (id)     => adminService.blogs.remove(id),

  getBooks:            (params) => adminService.books.getAll(params),
  getBook:             (id)     => adminService.books.getOne(id),
  createBook:          (d)      => adminService.books.create(d),
  updateBook:          (id, d)  => adminService.books.update(id, d),
  deleteBook:          (id)     => adminService.books.remove(id),

  getCategories:       ()       => adminService.categories.getAll(),
  getCategory:         (id)     => adminService.categories.getOne(id),
  createCategory:      (d)      => adminService.categories.create(d),
  updateCategory:      (id, d)  => adminService.categories.update(id, d),
  deleteCategory:      (id)     => adminService.categories.remove(id),

  getOrders:           (params) => adminService.orders.getAll(params),
  getOrderById:        (id)     => adminService.orders.getOne(id),
  updateOrderStatus:   (id, d)  => adminService.orders.updateStatus(id, d),
  getOrderStats:       (params) => adminService.orders.getStats(params),

  getBundles:           (params) => adminService.bundles.getAll({ ...params, includeInactive: true }),
  getBundle:            (id)     => adminService.bundles.getOne(id),
  createBundle:         (d)      => adminService.bundles.create(d),
  updateBundle:         (id, d)  => adminService.bundles.update(id, d),
  deleteBundle:         (id)     => adminService.bundles.remove(id),

  getStoreCategories:   (params) => adminService.storeCategories.getAll(params),
  createStoreCategory:  (d)      => adminService.storeCategories.create(d),
  updateStoreCategory:  (id, d)  => adminService.storeCategories.update(id, d),
  deleteStoreCategory:  (id)     => adminService.storeCategories.remove(id),

  getQuotes:            (params) => adminService.quotes.getAll(params),
  updateQuoteStatus:    (id, d)  => adminService.quotes.updateStatus(id, d),

  getCoupons:           (params) => adminService.coupons.getAll(params),
  createCoupon:         (d)      => adminService.coupons.create(d),
  updateCoupon:         (id, d)  => adminService.coupons.update(id, d),
  deleteCoupon:         (id)     => adminService.coupons.remove(id),

  uploadBookCover: (file) => {
    const formData = new FormData();
    formData.append('coverImage', file);
    return axiosInstance.postForm('/admin/books/upload-cover', formData).then((r) => r.data);
  },

  uploadBundleImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return axiosInstance.postForm('/admin/bundles/upload-image', formData).then((r) => r.data);
  },
};

export default adminService;
